import PaymentAccountCell from '@/components/Payment/PaymentAccountCell.vue'
import PaymentListOptions from '@/components/Payment/PaymentListOptions.vue'
import { Banknote, CircleUserRound, Clock, HandCoins, Info, Store } from 'lucide-vue-next'
import StatusBadge from 'pejer/src/components/StatusBadge.vue'
import { Groups, JSSchema } from 'pejer/src/tables'
import {
  CurrencyList,
  MoneyFormatter,
  PaymentEnvironmentFormatter,
  PaymentEnvironmentList,
  PaymentProviderList,
  PaymentStatuses,
  PaymentStatusFormatter,
} from 'pejer/src/tables/Global'
import { ColorPalette } from 'pejer/src/tables/helpers'
import { h } from 'vue'
import { paymentStatusLabels } from './paymentStatusLabels'

export namespace PaymentsEmbed {
  export interface Full extends ListItem {
    moneySplit: MoneySplit[]
  }

  export interface MoneySplit {
    splitID: number
    paymentReferenceID: string
    connectedAccountID: string
    connectedAccountExternalReferenceID: string
    amount: number
    description: string
    insertTime: string
    externalID: string
    isFee: boolean
  }

  export interface ListItem {
    paymentID: string
    paymentReferenceID: string
    connectedAccountIDSeller: string
    externalPaymentReferenceID: string
    description: string
    externalObjectIDTag: string
    environment: string
    paymentStatus: (typeof PaymentStatuses)[any]
    paymentStatusUpdateTime: string
    paymentProvider: string
    paymentGateway: string
    currency: string
    grossAmount: number
    insertTime: string

    customer: Customer
    connectedAccountSeller: ConnectedAccountSeller
    kidNorwayInvoice: KidNorwayInvoice | null
    fundsLocation?: FundsLocation
  }

  export type FundsLocation = 'Unknown' | 'BuckyClientAccount' | 'ClubAccount' | 'Stripe' | 'Dintero'

  export interface KidNorwayInvoice {
    invoiceUrl: string
    receiverIsDirectPaymentAccount: boolean
    kid?: string
    paidAmount?: number
  }

  export interface Customer {
    customerExternalID: string
    customerGuid: string
    customerType: string
    email: string
    mobile: string
    mobilePrefix: string
    name: string
    updateTime: string
  }

  export interface ConnectedAccountSeller {
    connectedAccountID: string
    businessName: string
    business_TaxID: string
    countryCode: string
    name: string
  }
}

export const PaymentsEmbedGroups = {
  Betaling: {
    icon: Banknote,
    color: ColorPalette.red,
    isMainGroup: true,
  },
  Konto: {
    icon: Store,
    color: ColorPalette.pink,
  },
  Kunde: {
    icon: CircleUserRound,
    color: ColorPalette.green,
  },
  Beløp: {
    icon: HandCoins,
    color: ColorPalette.green,
  },
  Betalingsdato: {
    icon: Clock,
    color: ColorPalette.black,
  },
  Status: {
    icon: Info,
    color: ColorPalette.brown,
  },
} satisfies Groups

export const PaymentsEmbedListSchema: JSSchema<PaymentsEmbed.ListItem, typeof PaymentsEmbedGroups> =
  {
    name: 'Payments Embed',
    title: 'Payments',
    groups: PaymentsEmbedGroups,
    fields: {
      paymentID: {
        type: 'string',
        humanReadable: 'Betalingsnummer',
        meta: {
          group: 'Betaling',
        },
      },
      paymentReferenceID: {
        type: 'string',
        humanReadable: 'Referanse-ID',
        meta: {
          group: 'Betaling',
        },
        // formattedCell: (value: string) => {
        //   return h(
        //     ColumnLink,
        //     {
        //       content: value,
        //       id: value,
        //       pathName: 'payment',
        //     },
        //     () => textWithIconCell(Banknote, value)
        //   )
        // },
      },
      externalPaymentReferenceID: {
        type: 'string',
        humanReadable: 'Ekstern referanse-ID',
        meta: {
          group: 'Betaling',
        },
      },
      connectedAccountIDSeller: {
        humanReadable: 'Selger-ID',
        relation: 'connectedAccounts.connectedAccountID',
        meta: {
          group: 'Konto',
        },
      },
      description: {
        type: 'string',
        humanReadable: 'Beskrivelse',
        meta: {
          group: 'Betaling',
          isMain: true,
        },
        sensitive: true,
        inSearchSelect: true,
      },
      externalObjectIDTag: {
        type: 'string',
        humanReadable: 'Ekstern objekt-ID-tag',
        meta: {
          group: 'Betaling',
        },
      },
      environment: {
        type: 'enum',
        humanReadable: 'Driftsmiljø',
        options: PaymentEnvironmentList,
        formatter: PaymentEnvironmentFormatter,
        meta: {
          group: 'Betaling',
        },
      },
      paymentStatus: {
        type: 'enum',
        humanReadable: 'Status',
        options: PaymentStatuses,
        formatter: PaymentStatusFormatter,
        meta: {
          group: 'Beløp',
          isMain: true,
          hideHeaderInCell: true,
          keepStyle: true,
        },
        formattedCell: (value: any) => {
          return h(StatusBadge, {
            status: value,
            label: paymentStatusLabels[value as keyof typeof paymentStatusLabels],
          })
        },
      },
      paymentGateway: {
        type: 'string',
        humanReadable: 'Betalingsmetode',
        meta: {
          group: 'Betaling',
        },
        metaType: 'badge',
      },
      paymentStatusUpdateTime: {
        type: 'date',
        humanReadable: 'Status sist oppdatert',
        meta: {
          group: 'Betaling',
        },
      },
      paymentProvider: {
        type: 'enum',
        humanReadable: 'Betalingsleverandør',
        options: PaymentProviderList,
        meta: {
          group: 'Betaling',
          hideFromTable: true,
        },
      },
      currency: {
        type: 'enum',
        humanReadable: 'Valuta',
        options: CurrencyList,
        meta: {
          group: 'Betaling',
        },
      },
      grossAmount: {
        type: 'number',
        humanReadable: 'Totalbeløp',
        formatter: MoneyFormatter,
        metaType: 'money',
        meta: {
          group: 'Beløp',
          isMain: true,
        },
      },
      insertTime: {
        type: 'date',
        humanReadable: 'Opprettet',
        meta: {
          group: 'Betalingsdato',
        },
        isDefaultSortColumn: true,
      },

      customer: {
        type: 'object',
        humanReadable: 'Kunde',
        meta: {
          group: 'Kunde',
        },
        values: {
          customerExternalID: {
            type: 'string',
            humanReadable: 'Ekstern ID',
            meta: {
              group: 'Kunde',
            },
          },
          customerGuid: {
            type: 'string',
            humanReadable: 'GUID',
            meta: {
              group: 'Kunde',
            },
          },
          customerType: {
            type: 'string',
            humanReadable: 'Kundetype',
            meta: {
              group: 'Kunde',
            },
          },
          email: {
            type: 'string',
            humanReadable: 'E-post',
            meta: {
              group: 'Kunde',
            },
          },
          mobile: {
            type: 'string',
            humanReadable: 'Mobil',
            meta: {
              group: 'Kunde',
            },
          },
          mobilePrefix: {
            type: 'string',
            humanReadable: 'Mobilprefiks',
            meta: {
              group: 'Kunde',
            },
          },
          name: {
            type: 'string',
            humanReadable: 'Navn',
            meta: {
              group: 'Kunde',
              isMain: true,
            },
            inSearchSelect: true,
          },
          updateTime: {
            type: 'date',
            humanReadable: 'Oppdateringstid',
            meta: {
              group: 'Kunde',
            },
          },
        },
      },
      connectedAccountSeller: {
        type: 'object',
        humanReadable: 'Selger',
        meta: {
          group: 'Konto',
        },
        values: {
          connectedAccountID: {
            type: 'string',
            humanReadable: 'ID',
            meta: {
              group: 'Konto',
            },
          },
          businessName: {
            type: 'string',
            humanReadable: 'Bedriftsnavn',
            meta: {
              group: 'Konto',
            },
          },
          business_TaxID: {
            type: 'string',
            humanReadable: 'Organisasjonsnummer',
            meta: {
              group: 'Konto',
            },
          },
          countryCode: {
            type: 'string',
            humanReadable: 'Landskode',
            meta: {
              group: 'Konto',
            },
          },
          name: {
            type: 'string',
            humanReadable: 'Alias navn',
            meta: {
              group: 'Konto',
              isMain: true,
            },
            specificCell:
              () =>
              ({ row }) =>
                h(PaymentAccountCell, { payment: row.original }),
          },
        },
      },
    },
    actions: {
      adjust: {
        cell: ({ row, emits }) => {
          return h(PaymentListOptions, {
            payment: row.original,
            'onUpdate:close': emits.reload,
          })
        },
      },
    },
  }

export const PaymentsEmbedFullSchema: JSSchema<PaymentsEmbed.Full, typeof PaymentsEmbedGroups> = {
  name: 'Payments Embed Full',
  title: 'Payment',
  groups: PaymentsEmbedGroups,
  fields: {
    ...(PaymentsEmbedListSchema.fields as unknown as JSSchema<
      PaymentsEmbed.Full,
      typeof PaymentsEmbedGroups
    >['fields']),
    moneySplit: {
      type: 'array',
      humanReadable: 'Delt beløp',
      meta: {
        group: 'Betaling',
      },
      values: {
        splitID: {
          type: 'number',
          humanReadable: 'Split-ID',
        },
        amount: {
          type: 'number',
          humanReadable: 'Beløp',
          formatter: MoneyFormatter,
          metaType: 'money',
        },
      },
    },
  },
}
